/**
 * ServiceOrderBundle — reusable sales widgets for every service landing page.
 *
 * Exports:
 *   - useCheckout(packageId)      — hook that fires POST /api/payments/checkout
 *   - BillingClarityBox           — explicit "mesačná platba, auto-renewal, zrušiteľné kedykoľvek" box
 *   - TrustBarSmall               — compact 4-point trust row (Zrušiteľné · GDPR · Stripe · bez poplatkov)
 *   - OrderNowBottomCTA           — big full-width bottom CTA, launches Stripe checkout
 *   - InlineBuyLink               — mid-page secondary CTA
 *
 * Design: dark theme compatible (bg transparent, text white). Uses Tailwind
 * utility classes already present in the app. Each exported element carries
 * stable data-testid attributes for the testing agent.
 */
import { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API } from "../../config";
import { useAuth } from "../../contexts/AuthContext";
import { trackEvent } from "../../hooks/useBehaviorTracker";
import { Lock, ShieldCheck, Repeat, X, ArrowRight, Loader2, Mail } from "lucide-react";

const MODAL_COPY = {
  sk: { title: "Kam ti pošleme prístup?", body: "Potrebujeme len tvoj email — Stripe ti potom pošle potvrdenie o platbe a my ti aktivujeme prístup do dashboardu.", placeholder: "tvoj@email.sk", submit: "Pokračovať na Stripe", foot: "Zrušiteľné kedykoľvek · v súlade s GDPR · zabezpečené cez Stripe", close: "Zavrieť", invalid: "Zadaj platný email.", noUrl: "Checkout URL nebolo vrátené.", failed: "Checkout zlyhalo." },
  cs: { title: "Kam vám pošleme přístup?", body: "Potřebujeme jen váš e-mail — Stripe vám pak pošle potvrzení o platbě a my vám aktivujeme přístup do dashboardu.", placeholder: "vas@email.cz", submit: "Pokračovat na Stripe", foot: "Zrušitelné kdykoli · v souladu s GDPR · zabezpečeno Stripe", close: "Zavřít", invalid: "Zadejte platný e-mail.", noUrl: "Platební odkaz nebyl vrácen.", failed: "Platba se nezdařila." },
};

export function useCheckout(packageId, lang = "sk") {
  const c = MODAL_COPY[lang] || MODAL_COPY.sk;
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Email prompt state — shown when the visitor is not signed in and
  // tries to click a buy button. Avoids the 500-blow-up when the backend
  // forwards an empty email to Stripe's checkout API.
  const [emailPromptOpen, setEmailPromptOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");

  const doCheckout = useCallback(async (emailToUse) => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
        user_email: (emailToUse || "").trim(),
        language: lang,
        metadata: {
          source_page: window.location.pathname,
          utm_source: new URLSearchParams(window.location.search).get("utm_source") || "",
          utm_campaign: new URLSearchParams(window.location.search).get("utm_campaign") || "",
        },
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
        return true;
      }
      setError(c.noUrl);
      return false;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || c.failed;
      setError(String(msg));
      return false;
    } finally {
      setLoading(false);
    }
  }, [packageId, lang, c]);

  const start = useCallback(async (overrideEmail) => {
    trackEvent("order_click", { package_id: packageId });
    const email = (overrideEmail || user?.email || "").trim();
    // If no email available, show the email-prompt modal instead of
    // sending an empty string to the backend (which would 500).
    if (!email) {
      setError(null);
      setEmailPromptOpen(true);
      return false;
    }
    return doCheckout(email);
  }, [doCheckout, user?.email, packageId]);

  const submitPendingEmail = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const email = pendingEmail.trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError(c.invalid);
      return;
    }
    setError(null);
    const ok = await doCheckout(email);
    if (ok) setEmailPromptOpen(false);
  }, [pendingEmail, doCheckout, c]);

  return {
    start,
    loading,
    error,
    emailPromptOpen,
    pendingEmail,
    setPendingEmail,
    submitPendingEmail,
    closeEmailPrompt: () => setEmailPromptOpen(false),
  };
}

/**
 * EmailPromptModal — rendered inline wherever useCheckout is used.
 * Collects an email from the anonymous visitor before firing Stripe.
 */
function EmailPromptModal({ open, email, setEmail, onSubmit, onClose, loading, error, lang = "sk" }) {
  if (!open) return null;
  const c = MODAL_COPY[lang] || MODAL_COPY.sk;
  return createPortal(
    <div
      data-testid="checkout-email-modal"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <form
        noValidate
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0f0b1a] p-6 sm:p-8 shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white"
          data-testid="checkout-email-modal-close"
          aria-label={c.close}
        >
          <X className="w-5 h-5" />
        </button>
        <div className="w-12 h-12 rounded-xl bg-[#a855f7]/15 flex items-center justify-center mb-4">
          <Mail className="w-6 h-6 text-[#c084fc]" />
        </div>
        <h3 className="text-xl font-black font-['Cabinet_Grotesk'] text-white mb-2">
          {c.title}
        </h3>
        <p className="text-sm text-white/70 mb-5">{c.body}</p>
        <input
          type="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={c.placeholder}
          className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#a855f7] mb-4"
          data-testid="checkout-email-modal-input"
        />
        {error && (
          <p className="-mt-2 mb-4 text-sm text-red-300" data-testid="checkout-email-modal-error">{error}</p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="checkout-email-modal-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#a855f7] hover:bg-[#9333ea] text-white rounded-lg font-black uppercase tracking-wider text-sm transition disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
          {c.submit}
        </button>
        <p className="mt-3 text-[11px] text-white/50 text-center">{c.foot}</p>
      </form>
    </div>,
    document.body
  );
}

/**
 * BillingClarityBox — explicit consumer-protection block.
 * Lists exactly what the customer pays, when, and how to cancel.
 */
export function BillingClarityBox({ priceEur, interval = "mes", serviceName, testid = "billing-clarity" }) {
  const monthly = interval === "mes" || interval === "month";
  return (
    <div
      data-testid={testid}
      className="mt-8 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 sm:p-6"
    >
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-emerald-400" />
        <h3 className="text-sm sm:text-base font-black uppercase tracking-wider text-emerald-300 font-['Chivo']">
          Čo presne platíš
        </h3>
      </div>
      <ul className="space-y-2 text-sm text-white/90">
        <li className="flex gap-2">
          <span className="text-emerald-400">•</span>
          <span>
            <strong className="text-white">€{priceEur}/{interval}</strong> za {serviceName || "túto službu"}
            {monthly ? " — strháva sa z karty raz mesačne" : " — jednorazová platba"}
          </span>
        </li>
        {monthly && (
          <>
            <li className="flex gap-2">
              <Repeat className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              <span><strong className="text-white">Auto-renewal:</strong> Stripe automaticky obnoví predplatné na konci každého mesačného cyklu.</span>
            </li>
            <li className="flex gap-2">
              <X className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              <span><strong className="text-white">Zrušiteľné kedykoľvek:</strong> jedným klikom v dashboarde cez Stripe Customer Portal. Prístup ti ostane do konca zaplateného obdobia.</span>
            </li>
          </>
        )}
        <li className="flex gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
          <span><strong className="text-white">Žiadne skryté poplatky.</strong> Žiadne setup fees. GDPR compliant. Stripe secure payments (PCI DSS).</span>
        </li>
      </ul>
    </div>
  );
}

export function TrustBarSmall({ testid = "trust-bar-small", lang = "sk" }) {
  return (
    <p
      data-testid={testid}
      className="mt-3 text-[11px] sm:text-xs text-white/60 text-center"
    >
      {lang === "cs" ? "Zrušitelné kdykoli · Žádné skryté poplatky · v souladu s GDPR · zabezpečeno Stripe" : "Zrušiteľné kedykoľvek · Žiadne skryté poplatky · v súlade s GDPR · zabezpečené cez Stripe"}
    </p>
  );
}

/**
 * InlineBuyLink — compact secondary CTA used mid-page.
 */
export function InlineBuyLink({ packageId, priceEur, interval = "mes", label, testid = "inline-buy-link" }) {
  const { start, loading, error, emailPromptOpen, pendingEmail, setPendingEmail, submitPendingEmail, closeEmailPrompt } = useCheckout(packageId);
  return (
    <>
      <button
        onClick={() => start()}
        disabled={loading}
        data-testid={testid}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-sm font-bold text-white transition disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        {label || `Objednať za €${priceEur}/${interval}`}
      </button>
      <EmailPromptModal
        open={emailPromptOpen}
        email={pendingEmail}
        setEmail={setPendingEmail}
        onSubmit={submitPendingEmail}
        onClose={closeEmailPrompt}
        loading={loading}
        error={error}
      />
    </>
  );
}

/**
 * OrderNowBottomCTA — the big full-width final CTA at the end of every page.
 * Launches Stripe checkout directly. Shows BillingClarityBox underneath so
 * the user sees exactly what they're committing to.
 */
export function OrderNowBottomCTA({
  packageId,
  priceEur,
  interval = "mes",
  serviceName,
  headline,
  subcopy,
  testid = "order-now-bottom",
}) {
  const { start, loading, error, emailPromptOpen, pendingEmail, setPendingEmail, submitPendingEmail, closeEmailPrompt } = useCheckout(packageId);
  const monthly = interval === "mes" || interval === "month";

  return (
    <section
      data-testid={testid}
      className="relative mt-16 mb-8 rounded-2xl bg-gradient-to-br from-[#a855f7]/20 via-[#1a1225] to-[#0a0a0f] border border-[#a855f7]/40 p-6 sm:p-10 overflow-hidden"
    >
      {/* decorative blob */}
      <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#a855f7]/20 rounded-full blur-3xl pointer-events-none" aria-hidden />
      <div className="relative z-10 max-w-2xl mx-auto text-center">
        <h2
          data-testid={`${testid}-headline`}
          className="text-3xl sm:text-4xl lg:text-5xl font-black font-['Cabinet_Grotesk'] text-white leading-tight mb-3"
        >
          {headline || `Objednať ${serviceName || "túto službu"}`}
        </h2>
        {subcopy && (
          <p className="text-base sm:text-lg text-white/80 mb-6 leading-relaxed">{subcopy}</p>
        )}

        <button
          onClick={() => start()}
          disabled={loading}
          data-testid={`${testid}-cta`}
          className="group inline-flex items-center gap-3 px-8 sm:px-12 py-5 bg-[#a855f7] hover:bg-[#9333ea] text-white rounded-xl font-black uppercase tracking-wider text-base sm:text-lg transition-all shadow-xl shadow-[#a855f7]/30 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              Presmerovanie na Stripe…
            </>
          ) : (
            <>
              Objednať teraz za €{priceEur}{monthly ? `/${interval}` : ""}
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>

        <TrustBarSmall testid={`${testid}-trust`} />

        {error && (
          <p className="mt-3 text-sm text-red-300" data-testid={`${testid}-error`}>
            {error}
          </p>
        )}

        <BillingClarityBox
          priceEur={priceEur}
          interval={interval}
          serviceName={serviceName}
          testid={`${testid}-clarity`}
        />
      </div>
      <EmailPromptModal
        open={emailPromptOpen}
        email={pendingEmail}
        setEmail={setPendingEmail}
        onSubmit={submitPendingEmail}
        onClose={closeEmailPrompt}
        loading={loading}
        error={error}
      />
    </section>
  );
}

/**
 * HeroBuyButton — primary CTA at the top of a service page.
 */
export function HeroBuyButton({ packageId, priceEur, interval = "mes", label, testid = "hero-buy-btn", lang = "sk" }) {
  const { start, loading, error, emailPromptOpen, pendingEmail, setPendingEmail, submitPendingEmail, closeEmailPrompt } = useCheckout(packageId, lang);
  return (
    <div className="inline-flex flex-col items-start">
      <button
        onClick={() => start()}
        disabled={loading}
        data-testid={testid}
        className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#a855f7] hover:bg-[#9333ea] text-white rounded-lg font-black uppercase tracking-wider text-sm transition shadow-lg shadow-[#a855f7]/30 disabled:opacity-60"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
        {label || `Objednať za €${priceEur}/${interval}`}
      </button>
      <TrustBarSmall testid={`${testid}-trust`} lang={lang} />
      <EmailPromptModal
        open={emailPromptOpen}
        email={pendingEmail}
        setEmail={setPendingEmail}
        onSubmit={submitPendingEmail}
        onClose={closeEmailPrompt}
        loading={loading}
        error={error}
        lang={lang}
      />
    </div>
  );
}
